import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Productivity from './pages/Productivity';
import Machines from './pages/Machines';
import Operators from './pages/Operators';
import Locations from './pages/Locations';
import SetoresOperacao from './pages/SetoresOperacao';
import WorkOrders from './pages/WorkOrders';
import Reports from './pages/Reports';
import Users from './pages/Users';
import MasterOverview from './pages/MasterOverview';
import MasterTenants from './pages/MasterTenants';
import Fuel from './pages/Fuel';
import OperationTypes from './pages/OperationTypes';
import ProductionEntry from './pages/ProductionEntry';
import ProductionReport from './pages/ProductionReport';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './components/AuthCallback';
import MasterUsers from './pages/MasterUsers';
import MasterRaioX from './pages/MasterRaioX';
import Fornecedores from './pages/Fornecedores';
import Colaboradores from './pages/Colaboradores';
import FinanceiroDashboard from './pages/FinanceiroDashboard';
import FinanceiroLancamentos from './pages/FinanceiroLancamentos';
import FinanceiroPlanoContas from './pages/FinanceiroPlanoContas';
import Inventario from './pages/Inventario';
import CentrosCusto from './pages/CentrosCusto';
import Almoxarifado from './pages/Almoxarifado';
import ContasPagar from './pages/ContasPagar';
import ContaCorrente from './pages/ContaCorrente';
import OfflineIndicator from './components/OfflineIndicator';
import useOffline from './hooks/useOffline';

function AppContent() {
  const { user, loading, logout, selectedTenantId, inTenantContext } = useAuth();
  const { online, syncing, pendingCount, syncAll, cacheServerData } = useOffline();

  // Cache data when user logs in
  React.useEffect(() => {
    if (user && online) {
      const token = localStorage.getItem('token');
      if (token) cacheServerData(token);
    }
  }, [user, online, cacheServerData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-400">Carregando...</span>
        </div>
      </div>
    );
  }

  // Google Auth callback
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (window.location.hash?.includes('session_id=')) {
    return (
      <Router>
        <AuthCallback />
        <Toaster position="bottom-right" richColors />
      </Router>
    );
  }

  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Login />} />
        </Routes>
        <Toaster position="bottom-right" richColors />
      </Router>
    );
  }

  const getDefaultRoute = () => {
    if (user.is_master) return '/overview';
    if (user.role === 'tecnico') return '/work-orders';
    return '/dashboard';
  };

  const ProtectedRoute = ({ children, allowedRoles }) => {
    // Master users must be in tenant context to access tenant pages
    if (user.is_master && !inTenantContext) {
      return <Navigate to="/overview" replace />;
    }
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to={getDefaultRoute()} replace />;
    }
    return children;
  };

  // Master without tenant context → redirect to overview
  const MasterGuard = ({ children }) => {
    if (user.is_master && (!selectedTenantId || !inTenantContext)) {
      return <Navigate to="/overview" replace />;
    }
    return children;
  };

  return (
    <Router>
      <Layout user={user} onLogout={logout}>
        <Routes>
          <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/dashboard" element={
            <MasterGuard>
              <ProtectedRoute allowedRoles={['admin', 'gestor', 'auxiliar_administrativo', 'supervisor', 'operador']}>
                <Dashboard user={user} key={selectedTenantId} />
              </ProtectedRoute>
            </MasterGuard>
          } />

          <Route path="/productivity" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'supervisor']}>
              <Productivity key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/machines" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'supervisor', 'tecnico']}>
              <Machines userRole={user.role} key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/operators" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'supervisor']}>
              <Operators userRole={user.role} key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/locations" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'supervisor']}>
              <Locations userRole={user.role} key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/work-orders" element={
            <MasterGuard>
              <WorkOrders userRole={user.role} user={user} key={selectedTenantId} />
            </MasterGuard>
          } />

          <Route path="/reports" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'supervisor']}>
              <Reports userRole={user.role} key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/fuel" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'auxiliar_administrativo', 'supervisor', 'tecnico']}>
              <Fuel userRole={user.role} key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/fornecedores" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor']}>
              <Fornecedores userRole={user.role} key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/colaboradores" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Colaboradores userRole={user.role} key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/financeiro" element={
            <ProtectedRoute allowedRoles={['admin', 'auxiliar_administrativo']}>
              <FinanceiroDashboard userRole={user.role} user={user} key={selectedTenantId} />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/lancamentos" element={
            <ProtectedRoute allowedRoles={['admin', 'auxiliar_administrativo']}>
              <FinanceiroLancamentos key={selectedTenantId} />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/plano-de-contas" element={
            <ProtectedRoute allowedRoles={['admin', 'auxiliar_administrativo']}>
              <FinanceiroPlanoContas key={selectedTenantId} />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/inventario" element={
            <ProtectedRoute allowedRoles={['admin', 'auxiliar_administrativo', 'gestor']}>
              <Inventario key={selectedTenantId} />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/centros-custo" element={
            <ProtectedRoute allowedRoles={['admin', 'auxiliar_administrativo', 'gestor']}>
              <CentrosCusto key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/almoxarifado" element={
            <ProtectedRoute allowedRoles={['admin', 'auxiliar_administrativo', 'gestor', 'supervisor']}>
              <Almoxarifado key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/contas-a-pagar" element={
            <ProtectedRoute allowedRoles={['admin', 'auxiliar_administrativo', 'gestor']}>
              <ContasPagar key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/conta-corrente" element={
            <ProtectedRoute allowedRoles={['admin', 'auxiliar_administrativo', 'gestor']}>
              <ContaCorrente key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/setores-operacao" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'supervisor']}>
              <SetoresOperacao key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Users key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/operation-types" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <OperationTypes key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/producao/apontamento" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'auxiliar_administrativo', 'supervisor']}>
              <ProductionEntry userRole={user.role} key={selectedTenantId} />
            </ProtectedRoute>
          } />

          <Route path="/producao/equiparacao" element={
            <ProtectedRoute allowedRoles={['admin', 'gestor', 'auxiliar_administrativo', 'supervisor']}>
              <ProductionReport key={selectedTenantId} />
            </ProtectedRoute>
          } />

          {user?.is_master && (
            <>
              <Route path="/overview" element={<MasterOverview />} />
              <Route path="/tenants" element={<MasterTenants />} />
            </>
          )}

          {user?.is_master && !user?.is_master_auxiliar && (
            <>
              <Route path="/master/usuarios" element={<MasterUsers />} />
              <Route path="/master/raiox" element={<MasterRaioX />} />
            </>
          )}

          <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
        </Routes>
      </Layout>

      <OfflineIndicator
        online={online}
        syncing={syncing}
        pendingCount={pendingCount}
        onSync={() => {
          const token = localStorage.getItem('token');
          if (token) syncAll(token);
        }}
      />
      <Toaster position="bottom-right" richColors />
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
